<template>
  <base-hm-input :id="id"
                 :class="[
                   $attrs['class'],
                   { 'has-inline-icon-left': hasSlot('icon-inline-left') },
                   'has-inline-icon-right',
                   { 'component__hm-input-money--inline': inline },
                   { 'component__hm-input-money--currency-clickable': currencyClickable && !disabledCombined },
                 ]"
                 :label="label"
                 :help="help"
                 :has-focus="hasFocus"
                 :is-empty="isValueEmpty"
                 :name="name"
                 :cols="cols"
                 :hidden="hidden"
                 :style="iconCountStyleVariables"
                 :disabled="disabledCombined">
    <input v-if="lazy"
           :id="`${id}-input`"
           v-model.trim.lazy="inputValue"
           :data-vv-as="label"
           :data-vv-name="name"
           :disabled="disabledCombined"
           class="hm-input__input hm-input__input--money"
           type="text"
           @focus="onFocus"
           @blur="onBlur">
    <input v-else
           :id="`${id}-input`"
           v-model.trim="inputValue"
           :data-vv-as="label"
           :data-vv-name="name"
           :disabled="disabledCombined"
           class="hm-input__input hm-input__input--money"
           type="text"
           @focus="onFocus"
           @blur="onBlur">
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper hm-input__icon__wrapper--left">
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-left"></slot>
    </div>
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper hm-input__icon__wrapper--right">
      <div :title="currencyTitle" class="hm-input__input__currency" @click="$emit('currency-click')" v-html="displayCurrency"></div>
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-right"></slot>
    </div>
  </base-hm-input>
</template>

<script>

import { isNil, trim, isArray, isFinite, isString, isEmpty, isNaN } from 'lodash';
import HmInputMixin from '@/mixins/HmInputMixin';
import parseDecimalNumber from 'parse-decimal-number';
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';

export default {
  name: 'HmInputMoney',

  mixins: [HmInputMixin, HotelmeisterMixin],

  props: {
    currency: {
      required: false,
      type: String,
      default: null,
    },
    inline: {
      required: false,
      type: Boolean,
      default: false,
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    lazy: {
      required: false,
      type: Boolean,
      default: false,
    },
    digits: {
      required: false,
      type: Number,
      default: 2,
    },
    currencyClickable: {
      required: false,
      type: Boolean,
      default: false,
    },
    currencyTitle: {
      required: false,
      type: String,
      default: null,
    },
    iconsRight: {
      required: false,
      type: Number,
      default: -1,
    },
    min: {
      required: false,
      type: Number,
      default: null,
    },
    max: {
      required: false,
      type: Number,
      default: null,
    },
  },

  data: function () {
    return {
      hover: false,
      inputValue: '',
    };
  },


  computed: {
    defaultValue: function () {
      return this.required ? 0 : null;
    },
    displayCurrency: function () {
      return this.currency || this.hotelSettings.details.currency;
    },
    iconCountLeft: function () {
      const slot = this.getSlot('icon-inline-left');

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    iconCountRight: function () {
      const slot = this.getSlot('icon-inline-right');

      if (this.iconsRight >= 0) return this.iconsRight + 1;

      if (isNil(slot)) return 0 + 1;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length + 1;
      else return 1 + 1;
    },
    iconCountStyleVariables: function () {
      return {
        '--icons-left': this.iconCountLeft,
        '--icons-right': this.iconCountRight,
      };
    },
  },

  watch: {
    internalValue: function (newVal) {
      this.inputValue = isNil(this.internalValue) || !isFinite(this.internalValue) ? this.defaultValue : `${this.internalValue.toLocaleString(
        this.$i18n.locale,
        {
          minimumFractionDigits: this.digits,
          maximumFractionDigits: this.digits,
        },
      )}`;
    },
    digits: function () {
      this.inputValue = isNil(this.internalValue) || !isFinite(this.internalValue) ? this.defaultValue : `${this.internalValue.toLocaleString(
        this.$i18n.locale,
        {
          minimumFractionDigits: this.digits,
          maximumFractionDigits: this.digits,
        },
      )}`;
    },
    '$i18n.locale': function () {
      this.inputValue = isNil(this.internalValue) || !isFinite(this.internalValue) ? this.defaultValue : `${this.internalValue.toLocaleString(
        this.$i18n.locale,
        {
          minimumFractionDigits: this.digits,
          maximumFractionDigits: this.digits,
        },
      )}`;
    },
  },

  created: function () {
    this.inputValue = isNil(this.internalValue) || !isFinite(this.internalValue) ? this.defaultValue : `${this.internalValue.toLocaleString(
      this.$i18n.locale,
      {
        minimumFractionDigits: this.digits,
        maximumFractionDigits: this.digits,
      },
    )}`;
  },

  mounted: function () {
    this.$on('focus', () => {
      this.$el.querySelector('input').select();
    });

    this.$on('blur', () => {
      const options = {
        thousands: (9999).toLocaleString(this.$i18n.locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[^\D]/g, ''),
        decimal: (0.1).toLocaleString(this.$i18n.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace(/[^\D]/g, ''),
      };

      const parsedTempValue = isNil(this.inputValue) ? null : parseDecimalNumber(this.inputValue, options);
      const tempValue = isNil(this.inputValue) || isNaN(parsedTempValue) ? this.defaultValue : parsedTempValue;

      let internalValue = isNil(tempValue) || !isFinite(tempValue) ? this.defaultValue : tempValue;
      if (!isNil(this.min)) internalValue = Math.max(this.min, internalValue);
      if (!isNil(this.max)) internalValue = Math.min(this.max, internalValue);

      this.internalValue = internalValue;

      if (!isNil(this.internalValue) || this.required) {
        this.internalValue = Math.round(internalValue * (10 ** this.digits)) / (10 ** this.digits);

        this.inputValue = isNil(this.internalValue) || !isFinite(this.internalValue) ? this.defaultValue : `${this.internalValue.toLocaleString(
          this.$i18n.locale,
          {
            minimumFractionDigits: this.digits,
            maximumFractionDigits: this.digits,
          },
        )}`;
      }
    });
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
   @import '~styles/components/lib/HmInput';
</style>

<style lang="scss">
  @import '~styles/import';

  .component__hm-input-money {
    .inline-button {
      width: 3.2rem;

      @include icon-color($middlegray);
      @include icon-hover($text);
      &:hover {
        cursor: pointer;
      }
    }

    &--currency-clickable {
      .hm-input__input__currency {
        pointer-events: all;
        cursor: pointer;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmInputMoney.vue